import { useCallback, useEffect, useState, type ReactNode } from 'react'

import * as api from '../api/kredensial.api.js'
import { ApiError } from '../api/client.js'

function pesan(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Tidak dapat menghubungi server'
  const detail = error.details?.map((d) => `${d.field}: ${d.message}`).join('; ')
  return detail !== undefined && detail !== '' ? detail : error.message
}

function tanggal(iso: string): string {
  return iso.slice(0, 10)
}

/**
 * Kredensial luring penguji — W1-A07.
 *
 * Halaman ini ada karena kodenya **hanya ditampilkan sekali**. Menerbitkan
 * lewat API tanpa antarmuka berarti kode berakhir di riwayat terminal atau
 * berkas sementara, dan operator yang menutup jendela sebelum mencetak harus
 * mencabut lalu menerbitkan ulang seluruhnya.
 */
export function KredensialLuringPage(): ReactNode {
  const [tuks, setTuks] = useState<readonly api.Tuk[]>([])
  const [tukId, setTukId] = useState('')
  const [rows, setRows] = useState<readonly api.Credential[]>([])
  const [hasil, setHasil] = useState<api.IssueResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const muat = useCallback(async (id: string) => {
    try {
      setRows(id === '' ? [] : await api.listCredentials(id))
      setError(null)
    } catch (err) {
      setError(pesan(err))
    }
  }, [])

  useEffect(() => {
    api
      .listTuk()
      .then((list) => {
        setTuks(list)
        const pertama = list[0]
        if (pertama !== undefined) {
          setTukId(pertama.id)
          void muat(pertama.id)
        }
      })
      .catch((err: unknown) => setError(pesan(err)))
  }, [muat])

  if (hasil !== null) {
    return (
      <LembarCetak
        hasil={hasil}
        tuk={tuks.find((t) => t.id === tukId)}
        onSelesai={() => {
          setHasil(null)
          void muat(tukId)
        }}
      />
    )
  }

  return (
    <div>
      <h1>Kredensial Luring Penguji</h1>
      <p className="hint">
        Dipakai Node Edge untuk memverifikasi penguji saat jaringan TUK putus. Diterbitkan sebelum
        gelombang, berlaku terbatas, dan terikat pada satu TUK.
      </p>

      <div className="page-actions">
        <label className="inline-label">
          TUK
          <select
            value={tukId}
            onChange={(e) => {
              setTukId(e.target.value)
              void muat(e.target.value)
            }}
          >
            {tuks.map((t) => (
              <option key={t.id} value={t.id}>
                {t.code} — {t.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error !== null && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {tukId !== '' && (
        <FormTerbit
          tukId={tukId}
          onTerbit={(r) => {
            setHasil(r)
            setError(null)
          }}
          onError={setError}
        />
      )}

      <h2>Kredensial di TUK ini</h2>
      {rows.length === 0 ? (
        <p className="dt-empty">Belum ada kredensial diterbitkan.</p>
      ) : (
        <div className="dt">
          <table>
            <thead>
              <tr>
                <th>Nomor</th>
                <th>Penguji</th>
                <th>Berlaku</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const dicabut = row.revokedAt !== null
                const habis = new Date(row.validUntil) < new Date()
                return (
                  <tr key={row.id}>
                    <td>
                      <code>{row.serial}</code>
                    </td>
                    <td>
                      {row.examiner.fullName}
                      <br />
                      <span className="hint">{row.examiner.code}</span>
                    </td>
                    <td>
                      {tanggal(row.validFrom)} – {tanggal(row.validUntil)}
                    </td>
                    <td>
                      {dicabut ? (
                        <span className="badge badge-ditolak" title={row.revokedReason ?? ''}>
                          DICABUT
                        </span>
                      ) : habis ? (
                        <span className="badge">KEDALUWARSA</span>
                      ) : (
                        <span className="badge badge-diterbitkan">AKTIF</span>
                      )}
                    </td>
                    <td>
                      {!dicabut && (
                        <Cabut
                          onCabut={(alasan) => {
                            api
                              .revoke(row.id, alasan)
                              .then(() => muat(tukId))
                              .catch((err: unknown) => setError(pesan(err)))
                          }}
                        />
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function FormTerbit({
  tukId,
  onTerbit,
  onError,
}: {
  tukId: string
  onTerbit: (hasil: api.IssueResult) => void
  onError: (pesan: string) => void
}): ReactNode {
  const [examiners, setExaminers] = useState<readonly api.Examiner[]>([])
  const [dipilih, setDipilih] = useState<readonly string[]>([])
  const [q, setQ] = useState('')
  const [validFrom, setValidFrom] = useState('')
  const [validUntil, setValidUntil] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    api
      .listExaminers(q)
      .then((list) => setExaminers(list.filter((e) => e.isActive)))
      .catch((err: unknown) => onError(pesan(err)))
  }, [q, onError])

  async function terbitkan(): Promise<void> {
    setBusy(true)
    try {
      onTerbit(
        await api.issue({
          tukId,
          examinerIds: dipilih,
          // Jendela mencakup hari penuh: penguji tiba sebelum sesi pertama dan
          // sesi terakhir berakhir sore hari.
          validFrom: `${validFrom}T00:00:00.000Z`,
          validUntil: `${validUntil}T23:59:59.000Z`,
        }),
      )
      setDipilih([])
    } catch (err) {
      onError(pesan(err))
    } finally {
      setBusy(false)
    }
  }

  const siap = dipilih.length > 0 && validFrom !== '' && validUntil !== ''

  return (
    <section className="terbit-form">
      <h2>Terbitkan</h2>
      <div className="terbit-window">
        <label>
          Berlaku dari
          <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
        </label>
        <label>
          Sampai
          <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
        </label>
      </div>

      <div className="dt-search">
        <input placeholder="Cari penguji…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <ul className="pilih-penguji">
        {examiners.map((e) => (
          <li key={e.id}>
            <label>
              <input
                type="checkbox"
                checked={dipilih.includes(e.id)}
                onChange={(ev) =>
                  setDipilih((prev) =>
                    ev.target.checked ? [...prev, e.id] : prev.filter((id) => id !== e.id),
                  )
                }
              />
              {e.fullName} <span className="hint">{e.code}</span>
            </label>
          </li>
        ))}
      </ul>

      <div className="page-actions">
        <button disabled={!siap || busy} onClick={() => void terbitkan()}>
          Terbitkan {dipilih.length > 0 ? `${dipilih.length} kredensial` : ''}
        </button>
        <span className="hint">
          Kredensial aktif yang sudah ada untuk penguji terpilih akan dicabut otomatis.
        </span>
      </div>
    </section>
  )
}

/**
 * Lembar cetak.
 *
 * Ditampilkan satu kali, menggantikan seluruh halaman. Tidak ada jalan kembali
 * selain tombol yang menyatakan bahwa lembarnya sudah dicetak — supaya menutup
 * halaman ini menjadi tindakan sadar, bukan kecelakaan.
 */
function LembarCetak({
  hasil,
  tuk,
  onSelesai,
}: {
  hasil: api.IssueResult
  tuk: api.Tuk | undefined
  onSelesai: () => void
}): ReactNode {
  const [sadar, setSadar] = useState(false)

  return (
    <div className="lembar">
      <div className="no-print">
        <h1>{hasil.diterbitkan} kredensial diterbitkan</h1>
        <p role="alert" className="peringatan">
          {hasil.peringatan}
        </p>
        {hasil.menggantikan > 0 && (
          <p className="hint">
            {hasil.menggantikan} kredensial lama untuk penguji yang sama telah dicabut otomatis.
          </p>
        )}
        <div className="page-actions">
          <button onClick={() => window.print()}>Cetak</button>
          <label className="inline-label">
            <input type="checkbox" checked={sadar} onChange={(e) => setSadar(e.target.checked)} />
            Saya sudah mencetak atau menyalin seluruh kode
          </label>
          <button disabled={!sadar} onClick={onSelesai}>
            Selesai
          </button>
        </div>
      </div>

      <div className="kartu-grid">
        {hasil.kredensial.map((k) => (
          <article key={k.serial} className="kartu">
            <div className="kartu-kepala">
              <strong>Kredensial Luring OSCE</strong>
              <code>{k.serial}</code>
            </div>
            <div className="kartu-nama">{k.fullName}</div>
            <div className="hint">{k.examinerCode}</div>
            <div className="kartu-kode">{k.code}</div>
            <div className="kartu-kaki">
              {tuk === undefined ? '' : `${tuk.code} — ${tuk.name}`}
              <br />
              Berlaku {tanggal(hasil.berlakuDari)} – {tanggal(hasil.berlakuSampai)}
            </div>
            <p className="kartu-catatan">
              Hanya untuk dipakai saat jaringan TUK terputus. Jangan difoto atau dibagikan.
            </p>
          </article>
        ))}
      </div>
    </div>
  )
}

function Cabut({ onCabut }: { onCabut: (alasan: string) => void }): ReactNode {
  const [open, setOpen] = useState(false)
  const [alasan, setAlasan] = useState('')

  if (!open) return <button onClick={() => setOpen(true)}>Cabut</button>

  return (
    <form
      className="inline-form"
      onSubmit={(e) => {
        e.preventDefault()
        onCabut(alasan)
        setOpen(false)
        setAlasan('')
      }}
    >
      <input
        value={alasan}
        onChange={(e) => setAlasan(e.target.value)}
        placeholder="Alasan pencabutan — wajib"
        required
      />
      <div className="inline-form-actions">
        <button type="submit">Cabut</button>
        <button type="button" onClick={() => setOpen(false)}>
          Batal
        </button>
      </div>
    </form>
  )
}
